import { Link } from 'react-router-dom'
import type { ShopTheme } from '../api/types'
import { useT } from '../i18n'
import { Zeichen } from './Zeichen'

/**
 * Die Bühne der Startseite: der Satz neben dem Bild, eingerückt und gerundet.
 *
 * Nebeneinander und nicht übereinander — Schrift auf einem Foto des Händlers
 * ist ein Glücksspiel, denn welches Bild kommt, weiß niemand. Auf der getönten
 * Fläche daneben ist der Satz in jeder Darstellung lesbar, und das Bild bleibt
 * ganz Bild.
 *
 * Eingerückt statt randlos (Faire, Selfridges): Ein randloses Bild muss auf
 * 2560 Pixeln bestehen, und die meisten Händlerfotos tun das nicht.
 *
 * Ohne Bild gibt es trotzdem eine Bühne — aus Text, auf der Farbe des Ladens.
 * Ein leerer Bildrahmen sähe aus, als wäre das Bild nicht geladen.
 *
 * Zwei Knöpfe: die Aufforderung des Händlers, und daneben der Weg in die
 * Sammlungen. Der zweite ist der Ausweg für jeden, den die erste nicht meint.
 */
export function Hero({ theme, shopName }: { theme?: ShopTheme | null; shopName: string }) {
  const t = useT()

  // Der Ladenname springt ein, wenn niemand eine Überschrift geschrieben hat:
  // Ein Bild ohne ein Wort daneben sagt nicht, in wessen Laden man steht.
  const ueberschrift = theme?.heroHeadline?.trim() || shopName
  const unterzeile = theme?.heroSubline?.trim() || theme?.tagline?.trim() || ''
  const knopfText = theme?.heroCtaLabel?.trim() || ''
  const ziel = theme?.heroCtaHref?.trim() || ''
  const bild = theme?.heroUrl || ''

  return (
    <section
      className="sf-held"
      data-mit-bild={bild ? 'ja' : 'nein'}
      aria-labelledby="held-ueberschrift"
    >
      <div className="sf-held-text">
        <h1 id="held-ueberschrift">{ueberschrift}</h1>
        {unterzeile && <p>{unterzeile}</p>}
        <div className="sf-held-aktionen">
          {knopfText && ziel ? (
            <Aufruf ziel={ziel} text={knopfText} />
          ) : (
            // Ohne eingestellten Knopf führt die Bühne ins Sortiment. Eine
            // Bühne ohne Weg ist Dekoration.
            <Link className="sf-knopf sf-knopf-haupt" to="/suche">
              {t('home.sortimentAnsehen')}
            </Link>
          )}
          <Link className="sf-knopf sf-knopf-neben" to="/sammlung">
            {t('home.sammlungenEntdecken')}
          </Link>
        </div>
      </div>

      {bild && (
        <div className="sf-held-bild">
          {/* Leeres alt: Das Bild ist Stimmung. Was es sagen soll, steht als
              Text daneben. Ohne loading="lazy": Es steht über der Falz, und
              ein verzögertes erstes Bild ist der sichtbarste Ladefehler, den
              eine Startseite haben kann. */}
          <img src={bild} alt="" fetchPriority="high" decoding="async" />
        </div>
      )}
    </section>
  )
}

/**
 * Der Knopf der Bühne.
 *
 * Innerhalb des Ladens über den Router, nach außen als gewöhnlicher Verweis:
 * Ein <Link> auf „https://…" hinge die Adresse an den Ladenpfad und führte auf
 * eine Seite, die es nicht gibt.
 */
function Aufruf({ ziel, text }: { ziel: string; text: string }) {
  const intern = ziel.startsWith('/') && !ziel.startsWith('//')

  if (intern) {
    return (
      <Link className="sf-knopf sf-knopf-haupt" to={ziel}>
        {text}
      </Link>
    )
  }

  return (
    <a className="sf-knopf sf-knopf-haupt" href={ziel} rel="noopener">
      {text}
    </a>
  )
}

/**
 * Die Zusagen unter der Bühne, als Karten mit Zeichen.
 *
 * Welches Zeichen, richtet sich nach der Stelle und nicht nach dem Text: Was
 * dort steht, schreibt der Händler, und ein Paket vor „Handgemacht im Allgäu"
 * wäre falsch geraten. Die übliche Reihenfolge — Versand, Zeit, Rückgabe —
 * stimmt öfter als jede Rateregel.
 */
const ZUSAGE_ZEICHEN = ['paket', 'uhr', 'rueckgabe'] as const

export function Zusagen({ texte }: { texte: string[] }) {
  const t = useT()
  if (texte.length === 0) return null

  return (
    <ul className="sf-zusagen" aria-label={t('home.zusagenAria')}>
      {texte.map((text, index) => (
        <li key={text}>
          <span className="sf-zusage-zeichen">
            <Zeichen art={ZUSAGE_ZEICHEN[index % ZUSAGE_ZEICHEN.length] ?? 'paket'} groesse={19} />
          </span>
          <span>{text}</span>
        </li>
      ))}
    </ul>
  )
}

/**
 * Der Satz des Ladens, groß, auf seiner Farbe — zwischen zwei Reihen Ware.
 *
 * Die Atempause, die aus zwei Rastern hintereinander eine Seite macht. Darunter
 * der Name, damit es ein Satz von jemandem ist und nicht ein Werbespruch.
 */
export function Leitsatz({ text, von }: { text: string; von: string }) {
  return (
    <figure className="sf-leitsatz">
      <p>{text}</p>
      <span>— {von}</span>
    </figure>
  )
}
